import React, { useEffect, useState } from "react";
import {
    Card, Typography, Table, Input, Select, Button, Space, Tag,
    Row, Col, Tabs, Empty, Form, Spin, Upload, Popconfirm, Image,
} from "antd";
import {
    PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, LinkOutlined,
    MoreOutlined, TeamOutlined, FileTextOutlined, UnorderedListOutlined,
    ClockCircleOutlined, AppstoreOutlined, CheckCircleOutlined, DownloadOutlined,
    DownOutlined, FilterOutlined, ArrowLeftOutlined, InboxOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { ENDPOINT } from "../../../Services/ENDPOINT";
import { useLocation, useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import dayjs from "dayjs";
import { RequirementService } from "../../../Services/Manage/RequirementService";
import { MeetingService } from "../../../Services/Manage/MeetingService";
import { DocumentService } from "../../../Services/Manage/DocumentService";
import { ProjectService } from "../../../Services/Manage/ProjectService";

import { UserService } from "../../../Services/Manage/UserService";

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

const { Title, Text } = Typography;
const { TextArea } = Input;
const STATUS_COLORS = {
    Approved: "#52c41a",
    Pending: "#fa8c16",
    "Change Request": "#1677ff",
    Draft: "#8c8c8c",
    Rejected: "#f5222d",
};

const PRIORITY_COLORS = {
    High: "#f5222d",
    Medium: "#fa8c16",
    Low: "#52c41a",
};

// Stat card — colored tint circle behind the icon, matching the reference layout
const StatCard = ({ icon, label, value, sub, color }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 78 }}>
        <div
            style={{
                width: 34, height: 34, borderRadius: "50%",
                background: `${color}1a`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, color: color || "#1677ff", marginBottom: 4,
            }}
        >
            {icon}
        </div>
        <div style={{ fontSize: 11, color: "#888" }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: color || "#222", lineHeight: 1.3 }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: "#1677ff", cursor: "pointer" }}>{sub}</div>}
    </div>
);

const RequirementGatheringDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const perm = getPermission("/overview-project");
    const viewAll = perm.view_all === true;
    const meetingPerm = getPermission("/overview-project/meetings");

    const [userMap, setUserMap] = useState({});
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await UserService.getAllUsers();
                const map = {};
                (res.data || []).forEach((u) => {
                    if (u.user_id) map[String(u.user_id)] = u.name;
                });
                setUserMap(map);
            } catch (error) {
                console.error(error);
            }
        })();
        (async () => {
            try {
                const user = await UserService.getOmeUser();
                setCurrentUser(user);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    const project = {
        id: location.state?.id || null, // numeric pro_id (Projects table PK)
        pro_name: location.state?.pro_name || "-",
        pro_no: location.state?.pro_no || "-",
        customer: location.state?.pro_customer || "-",
        manager: userMap[String(location.state?.pro_manager)] || location.state?.pro_manager || "-",
        value: location.state?.pro_value || 0,
        managerId: location.state?.pro_manager_id || null,
        engineerIds: location.state?.pro_engineer || [],

        start_date: (location.state?.pro_start_date && dayjs(location.state.pro_start_date).isValid())
            ? dayjs(location.state.pro_start_date).format("DD-MMM-YYYY")
            : "-",
        end_date: (location.state?.pro_target_end_date && dayjs(location.state.pro_target_end_date).isValid())
            ? dayjs(location.state.pro_target_end_date).format("DD-MMM-YYYY")
            : "-",
        status: location.state?.pro_status || "Not Started",
    };

    const [requirements, setRequirements] = useState([]);
    const [loading, setLoading] = useState(false);

   const [meetings, setMeetings] = useState([]);
    const [meetingsLoading, setMeetingsLoading] = useState(false);

    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [documentFileList, setDocumentFileList] = useState([]);
    const [documentUploading, setDocumentUploading] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);

    const [activeTab, setActiveTab] = useState("requirements");

    const [briefData, setBriefData] = useState(null);
    const [briefForm, setBriefForm] = useState({});
    const [briefEditing, setBriefEditing] = useState(false);
    const [briefLoading, setBriefLoading] = useState(false);
    const [briefSaving, setBriefSaving] = useState(false);

    const emptyBrief = {
        business_overview: "", project_objective: "", target_audience: "",
        project_scope: "", out_of_scope: "", competitors: "",
        success_criteria: "", important_notes: "",
    };

    useEffect(() => {
        fetchRequirements();
    }, []);


    useEffect(() => {
        if (project.id) fetchProjectBrief();
    }, [project.id]);

    useEffect(() => {
        if (!meetingPerm.view_all && !currentUser) return;
        fetchMeetings();
    }, [project.id, currentUser]);

    const canManageMeetings = () => {
        if (meetingPerm.view_all) return true;
        const uid = String(currentUser?.uid ?? "").trim();
        return uid && String(project.managerId ?? "").trim() === uid;
    };

    const fetchMeetings = async () => {
        try {
            setMeetingsLoading(true);
            const request = project.id
                ? await MeetingService.getAllByProject(project.id)
                : await MeetingService.getAllMeeting();

            if (request?.status) {
                const rows = (request.data || []).map((m) => {
                    let attendees = m.attendees;
                    if (typeof attendees === "string") {
                        try { attendees = JSON.parse(attendees || "[]"); } catch { attendees = []; }
                    }
                    return {
                        dbId: m.id,
                        meeting_code: m.meeting_code,
                        title: m.meeting_title,
                        meeting_date: m.meeting_date,
                        purpose_agenda: m.purpose_agenda,
                        organizer: m.organizer,
                        attendees: attendees || [],
                        status: m.status,
                        pro_id: m.pro_id,
                        added_by: m.added_by,
                    };
                });

                const visibleRows = meetingPerm.view_all
                    ? rows
                    : rows.filter((r) => {
                        const uid = String(currentUser?.uid ?? "").trim();
                        const uname = String(currentUser?.name ?? "").trim();
                        const isOrganizer = uname && r.organizer === uname;
                        const isAttendee = uname && Array.isArray(r.attendees) && r.attendees.includes(uname);
                        const isCreator = uid && String(r.added_by ?? "").trim() === uid;
                        return isOrganizer || isAttendee || isCreator;
                    });
                setMeetings(visibleRows);
            } else {
                setMeetings([]);
            }
        } catch (error) {
            console.error("Error fetching meetings:", error);
            setMeetings([]);
        } finally {
            setMeetingsLoading(false);
        }
    };

       useEffect(() => {
        if (project.id) fetchDocuments();
    }, [project.id]);

    const fetchDocuments = async () => {
        try {
            setDocumentsLoading(true);
            const request = await DocumentService.getAllByProject(project.id);
            if (request?.status) {
                setDocuments(request.data || []);
            } else {
                setDocuments([]);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    const handleUploadDocuments = async () => {
        const pending = documentFileList.filter((f) => f.originFileObj);
        if (pending.length === 0) return;
        try {
            setDocumentUploading(true);
            const user = await UserService.getOmeUser();
            for (const f of pending) {
                const formData = new FormData();
                formData.append("file", f.originFileObj);
                const res = await fetch(`${ENDPOINT.Document}/upload-file`, {
                    method: "POST",
                    body: formData,
                });
                const result = await res.json();
                if (result.status && result.success) {
                    await DocumentService.createDocument({
                        pro_id: project.id,
                        pro_no: project.pro_no,
                        file_name: result.file.filename,
                        file_path: result.file.path,
                        file_size: result.file.size,
                        added_by: user?.uid,
                    });
                }
            }
            setDocumentFileList([]);
            fetchDocuments();
        } catch (error) {
            console.error("Error uploading documents:", error);
        } finally {
            setDocumentUploading(false);
        }
    };

    const handleDeleteDocument = async (id) => {
        try {
            await DocumentService.deleteDocument(id);
            fetchDocuments();
        } catch (error) {
            console.error("Error deleting document:", error);
        }
    };

    const goAddMeeting = () =>
        navigate("/overview-project/meetings/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                pro_engineer: project.engineerIds,
            },
        });

    const goViewEditMeeting = (record, editable) =>
        navigate("/overview-project/meetings/add", {
            state: {
                id: record.pro_id || project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                meetingDbId: record.dbId,
                enableEdit: meetingPerm.edit ? editable : false,
                pro_engineer: project.engineerIds,
            },
        });

    const fetchProjectBrief = async () => {
        try {
            setBriefLoading(true);
            const request = await ProjectService.getAllProjectById(project.id);
            if (request?.status && request.data) {
                setBriefData(request.data);
                setBriefForm({ ...emptyBrief, ...request.data });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setBriefLoading(false);
        }
    };

    const handleBriefFieldChange = (field, value) => {
        setBriefForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleBriefSave = async () => {
        try {
            setBriefSaving(true);
            const payload = {
                business_overview: briefForm.business_overview,
                project_objective: briefForm.project_objective,
                target_audience: briefForm.target_audience,
                project_scope: briefForm.project_scope,
                out_of_scope: briefForm.out_of_scope,
                competitors: briefForm.competitors,
                success_criteria: briefForm.success_criteria,
                important_notes: briefForm.important_notes,
            };
            const request = await ProjectService.updateProject(payload, project.id);
            if (request?.status) {
                setBriefData(request.data);
                setBriefEditing(false);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setBriefSaving(false);
        }
    };

    const handleBriefCancel = () => {
        setBriefForm({ ...emptyBrief, ...(briefData || {}) });
        setBriefEditing(false);
    };

    const renderBulletList = (text) =>
        (text || "").split("\n").map((line) => line.trim()).filter(Boolean);

    const topRequirements = [...requirements]
        .sort((a, b) => (a.id > b.id ? 1 : -1))
        .slice(0, 5);

    const recentMeetings = [...meetings]
        .sort((a, b) => dayjs(b.meeting_date).valueOf() - dayjs(a.meeting_date).valueOf())
        .slice(0, 5);

    const fetchRequirements = async () => {
        try {
            setLoading(true);
            let request;
            if (project.id) {
                request = await RequirementService.getAllByProject(project.id);
            } else {
                request = await RequirementService.getAllRequirement();
            }

            if (request?.status) {
                let rawRows = request.data || [];

                if (!viewAll) {
                    const user = await UserService.getOmeUser();
                    const uid = String(user.uid).trim();
                    const uname = (user.name || "").trim().toLowerCase();

                    const isProjectManager = String(project.managerId ?? "").trim() === uid;

                    if (!isProjectManager) {
                        rawRows = rawRows.filter((r) => {
                            const addedByMatch = String(r.added_by ?? "").trim() === uid;
                            const requestedByMatch = (r.requested_by || "").trim().toLowerCase() === uname;
                            return addedByMatch || requestedByMatch;
                        });
                    }
                }

                const rows = rawRows.map((r) => ({
                    dbId: r.id,
                    id: r.req_id,
                    name: r.requirement_title,
                    category: r.category,
                    priority: r.priority,
                    status: r.status,
                    requested_by: r.requested_by,
                    added_on: r.created_at ? dayjs(r.created_at).format("DD-MMM-YYYY") : "-",
                }));
                setRequirements(rows);
            } else {
                setRequirements([]);
            }
        } catch (error) {
            console.error("Error fetching requirements:", error);
            setRequirements([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = requirements.filter((r) => {
        const text = searchText.toLowerCase();
        const matchesSearch = !text || (r.name || "").toLowerCase().includes(text) || (r.id || "").toLowerCase().includes(text);
        const matchesStatus = !statusFilter || r.status === statusFilter;
        const matchesPriority = !priorityFilter || r.priority === priorityFilter;
        const matchesCategory = !categoryFilter || r.category === categoryFilter;
        return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
    });

    const recentlyAdded = [...requirements]
        .sort((a, b) => (b.added_on > a.added_on ? 1 : -1))
        .slice(0, 3);

    const statusDistData = Object.keys(STATUS_COLORS).map((s) => ({
        name: s,
        value: requirements.filter((r) => r.status === s).length,
        color: STATUS_COLORS[s],
    }));
    const statusDistTotal = statusDistData.reduce((a, b) => a + b.value, 0);

    const categoryCounts = requirements.reduce((acc, r) => {
        if (!r.category) return acc;
        acc[r.category] = (acc[r.category] || 0) + 1;
        return acc;
    }, {});
    const topCategories = Object.entries(categoryCounts)
        .map(([name, count]) => ({ name, count, pct: requirements.length ? Math.round((count / requirements.length) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 7);

    const goAddRequirement = () =>
        navigate("/requirement-gathering/add", {
            state: {
                id: project.id, pro_name: project.pro_name, pro_no: project.pro_no, pro_manager_id: project.managerId,
                pro_engineer: project.engineerIds,
            },
        });

    const goViewEditRequirement = (record, editable) =>
        navigate("/requirement-gathering/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                reqDbId: record.dbId,
                action: editable ? "Edit" : "View",
                enableEdit: editable,
                pro_manager_id: project.managerId,
                pro_engineer: project.engineerIds,
            },
        });

    const columns = [
        { title: "ID", dataIndex: "id", key: "id", render: (t) => <Text strong>{t}</Text> },
        { title: "Requirement", dataIndex: "name", key: "name" },
        { title: "Category", dataIndex: "category", key: "category", render: (t) => t ? <Tag>{t}</Tag> : "-" },
        {
            title: "Priority", dataIndex: "priority", key: "priority",
            render: (v) => v ? (
                <span style={{
                    background: `${PRIORITY_COLORS[v] || "#bfbfbf"}1a`, color: PRIORITY_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v}</span>
            ) : "-",
        },
        {
            title: "Status", dataIndex: "status", key: "status",
            render: (v) => (
                <span style={{
                    background: `${STATUS_COLORS[v] || "#bfbfbf"}1a`, color: STATUS_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v || "-"}</span>
            ),
        },
        { title: "Requested By", dataIndex: "requested_by", key: "requested_by" },
        { title: "Added On", dataIndex: "added_on", key: "added_on" },
        {
            title: "Action", key: "action", align: "center",
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => goViewEditRequirement(record, false)} />
                    <Button size="small" icon={<EditOutlined />} onClick={() => goViewEditRequirement(record, true)} />
                    <Button size="small" icon={<LinkOutlined />} />
                    <Button size="small" icon={<MoreOutlined />} />
                </Space>
            ),
        },
    ];

    const requirementListTab = (
        <div style={{ padding: "0 16px 16px" }}>
            <Row gutter={16} align="top">
                <Col xs={24} lg={17}>
                    <Card
                        title={<Text strong style={{ fontSize: 16 }}>Requirement List</Text>}
                        extra={
                            <Button type="primary" icon={<PlusOutlined />} onClick={goAddRequirement}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                                Add Requirement
                            </Button>
                        }
                        style={{ marginBottom: 16 }}
                        bodyStyle={{ padding: 12 }}
                    >
                        {/* Filters — same Form.Item vertical-label pattern as ProjectsPage */}
                        <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label="Search Requirement" layout="vertical" style={{ marginBottom: 0 }}>
                                        <Input
                                            allowClear
                                            placeholder="Search requirement..."
                                            prefix={<SearchOutlined />}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item label="Status" layout="vertical" style={{ marginBottom: 0 }}>
                                        <Select allowClear placeholder="All Status" style={{ width: "100%" }}
                                            value={statusFilter} onChange={setStatusFilter}
                                            options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item label="Priority" layout="vertical" style={{ marginBottom: 0 }}>
                                        <Select allowClear placeholder="All Priority" style={{ width: "100%" }}
                                            value={priorityFilter} onChange={setPriorityFilter}
                                            options={Object.keys(PRIORITY_COLORS).map((s) => ({ value: s, label: s }))} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item label="Category" layout="vertical" style={{ marginBottom: 0 }}>
                                        <Select allowClear placeholder="All Category" style={{ width: "100%" }}
                                            value={categoryFilter} onChange={setCategoryFilter}
                                            options={[...new Set(requirements.map((r) => r.category).filter(Boolean))].map((c) => ({ value: c, label: c }))} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={3}>
                                    <Form.Item label=" " layout="vertical" style={{ marginBottom: 0 }}>
                                        <Space>
                                            <Button icon={<FilterOutlined />}>More</Button>
                                            <Button icon={<DownloadOutlined />} />
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            rowKey="dbId"
                            dataSource={filteredData}
                            columns={columns}
                            loading={loading}
                            pagination={{ pageSize: 7 }}
                        />
                    </Card>

                    {/* Recently Added Requirements */}
                    <Card
                        title={<Text strong style={{ fontSize: 15 }}>Recently Added Requirements</Text>}
                        extra={<a>View All</a>}
                        bodyStyle={{ padding: "6px 16px" }}
                    >
                        {recentlyAdded.length === 0 && <Empty description="No requirements yet" />}
                        {recentlyAdded.map((r, i) => (
                            <div
                                key={r.dbId}
                                style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "10px 0", borderBottom: i !== recentlyAdded.length - 1 ? "1px solid #f5f5f5" : "none",
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <Text strong style={{ fontSize: 12 }}>{r.id}</Text>{" "}
                                    <Text style={{ fontSize: 12 }}>{r.name}</Text>
                                </div>
                                <Space size={8}>
                                    <span style={{
                                        background: `${PRIORITY_COLORS[r.priority] || "#bfbfbf"}1a`, color: PRIORITY_COLORS[r.priority] || "#595959",
                                        padding: "2px 10px", borderRadius: 6, fontWeight: 600, fontSize: 11,
                                    }}>{r.priority || "-"}</span>
                                    <span style={{
                                        background: `${STATUS_COLORS[r.status] || "#bfbfbf"}1a`, color: STATUS_COLORS[r.status] || "#595959",
                                        padding: "2px 10px", borderRadius: 6, fontWeight: 600, fontSize: 11,
                                    }}>{r.status || "-"}</span>
                                    <Text type="secondary" style={{ fontSize: 11, width: 80, textAlign: "right" }}>{r.added_on}</Text>
                                    <Text type="secondary" style={{ fontSize: 11, width: 60 }}>{r.requested_by}</Text>
                                    <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => goViewEditRequirement(r, false)} />
                                </Space>
                            </div>
                        ))}
                    </Card>
                </Col>

                <Col xs={24} lg={7}>
                    <Card title={<Text strong style={{ fontSize: 15 }}>Requirements by Status</Text>} style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <div style={{ width: 110, height: 110, flexShrink: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <RechartsTooltip contentStyle={{ fontSize: 11 }} />
                                        <Pie data={statusDistData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={52} paddingAngle={2}>
                                            {statusDistData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div style={{ flex: 1 }}>
                                {statusDistData.map((e, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5, marginBottom: 8 }}>
                                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: e.color, display: "inline-block" }} />
                                            {e.name}
                                        </span>
                                        <Text strong style={{ fontSize: 12.5 }}>
                                            {e.value} ({statusDistTotal ? Math.round((e.value / statusDistTotal) * 100) : 0}%)
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>Total: {statusDistTotal}</Text>
                    </Card>

                    <Card title="Top Requirement Categories" style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                        {topCategories.length === 0 && <Empty description="No data" />}
                        {topCategories.map((c, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                                    <span>{c.name}</span>
                                    <Text strong style={{ fontSize: 12 }}>{c.count} ({c.pct}%)</Text>
                                </div>
                                <div style={{ height: 5, background: "#f0f0f0", borderRadius: 3 }}>
                                    <div style={{ width: `${c.pct}%`, height: "100%", background: "#1677ff", borderRadius: 3 }} />
                                </div>
                            </div>
                        ))}
                    </Card>

                    <Card title="Quick Links" bodyStyle={{ padding: 12 }}>
                        <Space direction="vertical" size={8}>
                            <a>Create Task from Requirement</a>
                            <a>Create Milestone from Requirement</a>
                            {meetingPerm.add ? (
                                <a onClick={goAddMeeting}>Add Meeting</a>
                            ) : (
                                <a style={{ color: "#bfbfbf", cursor: "not-allowed" }}>Add Meeting</a>
                            )}
                            <a>Upload Document</a>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );

    const projectBriefTab = (
        <div style={{ padding: "0 16px 16px" }}>
            <Row gutter={16} align="top">
                <Col xs={24} lg={17}>
                    <Card
                        title={<Text strong style={{ fontSize: 16 }}>Project Brief</Text>}
                        extra={
                            briefEditing ? (
                                <Space>
                                    <a onClick={handleBriefCancel}>Cancel</a>
                                    <a onClick={handleBriefSave}>{briefSaving ? "Saving..." : "Save"}</a>
                                </Space>
                            ) : (
                                <a onClick={() => setBriefEditing(true)}>Edit</a>
                            )
                        }
                        loading={briefLoading}
                    >
                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Business Overview</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.business_overview}
                                        onChange={(e) => handleBriefFieldChange("business_overview", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.business_overview || "-"}</div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Project Objective</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.project_objective}
                                        onChange={(e) => handleBriefFieldChange("project_objective", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.project_objective || "-"}</div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Target Audience</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.target_audience}
                                        onChange={(e) => handleBriefFieldChange("target_audience", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.target_audience || "-"}</div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Competitors</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.competitors}
                                        onChange={(e) => handleBriefFieldChange("competitors", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.competitors || "-"}</div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Project Scope</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} placeholder="One item per line" value={briefForm.project_scope}
                                        onChange={(e) => handleBriefFieldChange("project_scope", e.target.value)} />
                                ) : (
                                    <div style={{ marginTop: 4 }}>
                                        {renderBulletList(briefData?.project_scope).map((line, i) => (
                                            <div key={i} style={{ fontSize: 12, color: "#52c41a" }}>✓ {line}</div>
                                        ))}
                                        {!briefData?.project_scope && <div style={{ fontSize: 13 }}>-</div>}
                                    </div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Out of Scope</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} placeholder="One item per line" value={briefForm.out_of_scope}
                                        onChange={(e) => handleBriefFieldChange("out_of_scope", e.target.value)} />
                                ) : (
                                    <div style={{ marginTop: 4 }}>
                                        {renderBulletList(briefData?.out_of_scope).map((line, i) => (
                                            <div key={i} style={{ fontSize: 12, color: "#f5222d" }}>⊖ {line}</div>
                                        ))}
                                        {!briefData?.out_of_scope && <div style={{ fontSize: 13 }}>-</div>}
                                    </div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Success Criteria</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.success_criteria}
                                        onChange={(e) => handleBriefFieldChange("success_criteria", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.success_criteria || "-"}</div>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Important Notes</Text>
                                {briefEditing ? (
                                    <TextArea rows={3} value={briefForm.important_notes}
                                        onChange={(e) => handleBriefFieldChange("important_notes", e.target.value)} />
                                ) : (
                                    <div style={{ fontSize: 13, marginTop: 4 }}>{briefData?.important_notes || "-"}</div>
                                )}
                            </Col>
                        </Row>
                    </Card>
                </Col>

                <Col xs={24} lg={7}>
                    <Card title={<Text strong style={{ fontSize: 15 }}>Top Requirements</Text>} bodyStyle={{ padding: 12 }}>
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="dbId"
                            dataSource={topRequirements}
                            columns={[
                                { title: "ID", dataIndex: "id", key: "id", width: 70, render: (t) => <Text strong style={{ fontSize: 12 }}>{t}</Text> },
                                { title: "Requirement", dataIndex: "name", key: "name", render: (t) => <span style={{ fontSize: 12 }}>{t}</span> },
                                {
                                    title: "Priority", dataIndex: "priority", key: "priority", width: 70,
                                    render: (v) => v ? <span style={{ color: PRIORITY_COLORS[v] || "#595959", fontSize: 12, fontWeight: 600 }}>{v}</span> : "-"
                                },
                                {
                                    title: "Status", dataIndex: "status", key: "status", width: 90,
                                    render: (v) => <span style={{ color: STATUS_COLORS[v] || "#595959", fontSize: 12, fontWeight: 600 }}>{v || "-"}</span>
                                },
                            ]}
                        />
                        <div style={{ textAlign: "right", marginTop: 8 }}>
                            <a onClick={() => setActiveTab("requirements")}>View All Requirements</a>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} align="top" style={{ marginTop: 16 }}>
                <Col xs={24}>
                    <Card
                        title={<Text strong style={{ fontSize: 15 }}>Recent Meetings</Text>}
                        extra={<a onClick={() => setActiveTab("meetings")}>View All</a>}
                        bodyStyle={{ padding: 12 }}
                    >
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="dbId"
                            loading={meetingsLoading}
                            dataSource={recentMeetings}
                            locale={{ emptyText: <Empty description="No meetings yet" /> }}
                            columns={[
                                { title: "Meeting #", dataIndex: "meeting_code", key: "meeting_code", width: 100, render: (t) => <Text strong style={{ fontSize: 12 }}>{t}</Text> },
                                { title: "Date", dataIndex: "meeting_date", key: "meeting_date", width: 110, render: (v) => <span style={{ fontSize: 12 }}>{v ? dayjs(v).format("DD-MMM-YYYY") : "-"}</span> },
                                { title: "Purpose", dataIndex: "purpose_agenda", key: "purpose_agenda", render: (t) => <span style={{ fontSize: 12 }}>{t || "-"}</span> },
                                { title: "Attendees", dataIndex: "attendees", key: "attendees", render: (v) => <span style={{ fontSize: 12 }}>{Array.isArray(v) && v.length ? v.join(", ") : "-"}</span> },
                                {
                                    title: "Action", key: "action", align: "center", width: 80,
                                    render: (_, record) => (
                                        <Button size="small" icon={<EyeOutlined />} onClick={() => goViewEditMeeting(record, false)} />
                                    ),
                                },
                            ]}
                        />
                        <div style={{ marginTop: 12 }}>
                            {meetingPerm.add ? (
                                <Button icon={<PlusOutlined />} onClick={goAddMeeting}
                                    style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}>
                                    Add Meeting
                                </Button>
                            ) : (
                                <Button icon={<PlusOutlined />} disabled>Add Meeting</Button>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );


 const placeholderTab = (label) => (
        <div style={{ padding: "0 16px 16px" }}>
            <Card>
                <Empty />
            </Card>
        </div>
    );

const meetingNotesTab = (
        <div style={{ padding: "0 16px 12px" }}>
            <Card
                title={<Text strong style={{ fontSize: 15 }}>Meeting Notes</Text>}
                bodyStyle={{ padding: 12 }}
                extra={ 
                    meetingPerm.add ? (
                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={goAddMeeting}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                            Add Meeting
                        </Button>
                    ) : null
                }
            >
                <Table
                    size="small"
                    rowKey="dbId"
                    loading={meetingsLoading}
                    dataSource={meetings}
                    pagination={{ pageSize: 7, size: "small" }}
                    locale={{ emptyText: <Empty description="No meetings yet" /> }}
                    columns={[
                        { title: "Meeting #", dataIndex: "meeting_code", key: "meeting_code", width: 110, render: (t) => <Text strong style={{ fontSize: 12.5 }}>{t}</Text> },
                        { title: "Date", dataIndex: "meeting_date", key: "meeting_date", width: 110, render: (v) => <span style={{ fontSize: 12.5 }}>{v ? dayjs(v).format("DD-MMM-YYYY") : "-"}</span> },
                        { title: "Purpose", dataIndex: "purpose_agenda", key: "purpose_agenda", render: (t) => <span style={{ fontSize: 12.5 }}>{t || "-"}</span> },
                        { title: "Organizer", dataIndex: "organizer", key: "organizer", width: 120, render: (t) => <span style={{ fontSize: 12.5 }}>{t || "-"}</span> },
                        { title: "Attendees", dataIndex: "attendees", key: "attendees", render: (v) => <span style={{ fontSize: 12.5 }}>{Array.isArray(v) && v.length ? v.join(", ") : "-"}</span> },
                        {
                            title: "Action", key: "action", align: "center", width: 90,
                            render: (_, record) => (
                                <Space size="small">
                                    <Button size="small" icon={<EyeOutlined />} onClick={() => goViewEditMeeting(record, false)} />
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );

  const documentsTab = (
        <div style={{ padding: "0 16px 16px" }}>
            <Card title={<Text strong style={{ fontSize: 15 }}>Documents</Text>} bodyStyle={{ padding: 12 }}>
      <Upload
                    multiple
                    listType="picture-card"
                    fileList={documentFileList}
                    accept="image/*"
                    beforeUpload={(file) => {
                        setDocumentFileList((prev) => [
                            ...prev,
                            { uid: `${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file, url: URL.createObjectURL(file) },
                        ]);
                        return false;
                    }}
                    onRemove={(file) => setDocumentFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
                >
                    <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                    </div>
                </Upload>

                {documentFileList.some((f) => f.originFileObj) && (
                    <div style={{ marginTop: 4, marginBottom: 16 }}>
                        <Button
                            type="primary"
                            loading={documentUploading}
                            onClick={handleUploadDocuments}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            Save Uploaded Images
                        </Button>
                    </div>
                )}

                {documentsLoading ? (
                    <Empty description="Loading..." />
                ) : documents.length === 0 ? (
                    <Empty description="No documents yet" />
                ) : (
                    <Image.PreviewGroup>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {documents.map((d) => (
                                <div key={d.id} style={{ position: "relative", width: 120 }}>
                                    <Image
                                        src={d.file_path?.startsWith("http") ? d.file_path : `${ENDPOINT.base}/${d.file_path}`}
                                        width={120}
                                        height={120}
                                        style={{ objectFit: "cover", borderRadius: 6 }}
                                    />
                                    <Popconfirm title="Delete this image?" onConfirm={() => handleDeleteDocument(d.id)} okText="Delete" cancelText="Cancel">
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            style={{ position: "absolute", top: 4, right: 4 }}
                                        />
                                    </Popconfirm>
                                </div>
                            ))}
                        </div>
                    </Image.PreviewGroup>
                )}
            </Card>
        </div>
    );

    const tabItems = [
        { key: "brief", label: "Project Brief", children: projectBriefTab },
        { key: "requirements", label: "Requirement List", children: requirementListTab },
        { key: "meetings", label: "Meeting Notes", children: meetingNotesTab },
        { key: "documents", label: "Documents", children: documentsTab },
        { key: "screens", label: "Screens / Pages", children: placeholderTab("Screens / Pages") },
        { key: "features", label: "Features", children: placeholderTab("Features") },

    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                    <Space align="center" size={8}>
                        <ArrowLeftOutlined
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}
                        />
                        <Typography.Title level={4} style={{ margin: 0, color: "#1658b4" }}>Requirement Gathering</Typography.Title>
                        <Tag color="green">{project.status}</Tag>
                    </Space>
                    <div>
                        {/* <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Dashboard &gt; Project Management &gt; Projects &gt; {project.pro_name} &gt; Requirement Gathering
                        </Typography.Text> */}
                    </div>
                </div>
                <Space wrap>
             <Space wrap>
                    <Button size="middle" onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}>View Project</Button>
                    {/* <Button size="middle" onClick={() => navigate("/projects/edit")}>Edit Project</Button> */}
                    <Button size="middle" icon={<DownOutlined />}>More Actions</Button>
                </Space>
                </Space>
            </div>

            {/* Project info + stat cards — ONE bordered box, matching the reference layout */}
            <div style={{ padding: "12px 16px 0" }}>
                <Card bodyStyle={{ padding: "14px 20px" }}>
                    <Row align="middle" gutter={[16, 12]}>
                        <Col xs={24} md={12}>
                            <Space align="start" size={12}>
                                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f9f0ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📁</div>
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 15 }}>{project.pro_name}</Text>
                                        <Tag color="cyan" style={{ fontSize: 10, marginLeft: 10 }}>{project.pro_no}</Tag>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Customer</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.customer}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Manager</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.manager}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Value</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>₹{Number(project.value).toLocaleString("en-IN")}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Start Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.start_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project End Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.end_date}</div>
                                        </div>
                                    </div>
                                </div>
                            </Space>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                                <div onClick={() => setActiveTab("meetings")} style={{ cursor: "pointer" }}>
                                    <StatCard label="Meetings" value={meetings.length} sub="View Details" icon={<TeamOutlined />} color="#7c3aed" />
                                </div>
                                <div style={{ width: 1, height: 40, background: "#e8e8e8", margin: "0 16px" }} />

                                <div onClick={() => setActiveTab("documents")} style={{ cursor: "pointer" }}>
                                    <StatCard label="Documents" value={documents.length} sub="View Documents" icon={<FileTextOutlined />} color="#52c41a" />
                                </div>                                <div style={{ width: 1, height: 40, background: "#e8e8e8", margin: "0 16px" }} />
                                <StatCard label="Requirements" value={requirements.length} sub={`${requirements.filter((r) => r.status === "Approved").length} Approved`} icon={<UnorderedListOutlined />} color="#fa8c16" />
                                <div style={{ width: 1, height: 40, background: "#e8e8e8", margin: "0 16px" }} />
                                <StatCard label="Pending" value={requirements.filter((r) => r.status === "Pending").length} sub="View Pending" icon={<ClockCircleOutlined />} color="#f5222d" />
                                <div style={{ width: 1, height: 40, background: "#e8e8e8", margin: "0 16px" }} />
                                <StatCard label="Features" value={0} sub="View Features" icon={<AppstoreOutlined />} color="#722ed1" />
                                {/* <div style={{ width: 1, height: 40, background: "#e8e8e8", margin: "0 16px" }} /> */}
                                {/* <StatCard label="Approval Status" value="Pending" sub="Awaiting Approval" icon={<CheckCircleOutlined />} color="#13c2c2" /> */}
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* Tabs — all 7 in one line like the reference */}
            <div style={{ padding: "10px 16px 0" }}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
            </div>
        </Card>
    );
};

export default RequirementGatheringDetail;